import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import { useAppContext } from "./AppContext";
import { toast } from "react-toastify";

interface UserContextType {
  userData: UserData;
  setUserData: React.Dispatch<React.SetStateAction<any>>;
  fetchUserData: () => Promise<void>;
}
interface UserData {
  email: string;
  emailValidatedOn: string;
  firstName: string;
  id: string;
  lastName: string;
  middleName: string;
  profileImageUrl: string;
  scnNumber: string;
  userRole: string;
  username: string;
  mobile: string;
  address: string;
}
interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<any>(() => {
    const savedData = sessionStorage.getItem("userData");
    return savedData ? JSON.parse(savedData) : {};
  });

  const { handleSignOut } = useAppContext();

    const handleSignOutWithError = useCallback(() => {
    handleSignOut();
    toast.error("Plese Login again", {
      position: "bottom-right",
    });
  },[handleSignOut])

  const fetchUserData = useCallback(async () => {
    const authToken = sessionStorage.getItem("token");
    const isLoggedIn = sessionStorage.getItem("isLoggedIn") === "true";

    // Only call handleSignOutWithError if the user is supposed to be logged in
    if (isLoggedIn && !authToken) {
      handleSignOutWithError();
      return;
    }

    // Proceed to fetch user data if user is logged in
    if (isLoggedIn || authToken) {
      try {
        const response = await fetch(
          "https://backend-kyw8.onrender.com/get-current-user",
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Unauthorized. Please login again.");
          }
          handleSignOutWithError();
          return;
        }

        const data = await response.json();
        setUserData(data.user);
        console.log("fetchUserData:", data.user);
        sessionStorage.setItem("userData", JSON.stringify(data.user));
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error("fetchUserData Error:", error.message);
          handleSignOutWithError();
        } else {
          console.error("fetchUserData Error:", error);
          handleSignOutWithError();
        }
      }
    }
  },[handleSignOutWithError]);

  useEffect(() => {
    fetchUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserContext.Provider value={{ userData, setUserData, fetchUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

export { UserProvider };
